import {useInfiniteQuery} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

/**
 * Fetches customer orders from the order history API endpoint using provided configuration and authentication.
 *
 * @param {string} appOrigin - The base URL of the application or API origin.
 * @param {string} token - The Bearer token used for API authentication.
 * @param {Object} config - Configuration object containing proxy path and parameters.
 * @param {string} config.proxyPath - The proxy path to prepend to the API endpoint.
 * @param {Object} config.parameters - Parameters for the API request.
 * @param {string} config.parameters.organizationId - The organization ID for the request.
 * @param {string} config.parameters.siteId - The site ID for the request.
 * @param {string} orderType - The type of orders to fetch (e.g., 'store', 'online').
 * @param {number} pageParam - The page number for paginated results.
 * @returns {Promise<Object>} - A promise that resolves to the JSON response containing customer orders.
 * @throws {Error} Throws an error if the network response is not ok.
 */
const fetchCustomerOrders = async (appOrigin, token, config, orderType, pageParam) => {
    const {
        proxyPath,
        parameters: {organizationId, siteId}
    } = config

    const url = `${appOrigin}${proxyPath}/custom/order-history/v1/organizations/${organizationId}/order-history?siteId=${siteId}`

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
            orderType,
            ...((orderType === 'store' || pageParam > 1) && {
                pageNumber: pageParam
            })
        })
    })

    if (!response.ok) {
        throw new Error('Failed to fetch orders')
    }

    return response.json()
}

/**
 * React hook for fetching and paginating a customer's order history using infinite queries.
 *
 * @param {string} orderType - The type of orders to retrieve (e.g., 'online', 'store').
 * @param {string} customerId - The unique identifier of the customer whose orders are being fetched.
 * @param {Object} [queryOptions={}] - Optional configuration to override or extend the default query options for useInfiniteQuery.
 * @returns {Object} - The result object from useInfiniteQuery, containing order data, loading state, error state, and pagination helpers.
 */
export const useCustomerOrders = (orderType, customerId, queryOptions = {}) => {
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const {
        app: {commerceAPI: config}
    } = getConfig()
    const {
        site: {id: siteId}
    } = useMultiSite()
    const onClient = typeof window !== 'undefined'

    return useInfiniteQuery({
        queryKey: ['order-history', siteId, orderType, customerId],
        queryFn: async ({pageParam = 1}) => {
            const token = await getTokenWhenReady()
            return fetchCustomerOrders(appOrigin, token, config, orderType, pageParam)
        },
        getNextPageParam: (lastPage, allPages) => {
            return lastPage?.hasNext ? allPages.length + 1 : undefined
        },
        enabled: onClient && !!customerId && !!siteId,
        staleTime: 1000 * 60,
        // Keep previous data while refetching to prevent UI flash/disappear
        placeholderData: (previousData) => previousData,
        // Don't refetch on window focus to prevent unnecessary data loss
        refetchOnWindowFocus: false,
        ...queryOptions
    })
}
